import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Download } from "lucide-react";
import { fetchContent, addPressAsset, deletePressAsset } from "@/lib/api";
import UploadField from "@/components/UploadField";

const inputCls =
    "w-full bg-paper border-2 border-ink px-3.5 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-pink transition-shadow duration-150";

export default function PressManager() {
    const [assets, setAssets] = useState(null);
    const [form, setForm] = useState({ label: "", note: "", url: "" });
    const [showForm, setShowForm] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = () =>
        fetchContent()
            .then((d) => setAssets(d.press_assets || []))
            .catch(() => setAssets([]));

    useEffect(() => {
        load();
    }, []);

    const add = async (e) => {
        e.preventDefault();
        if (!form.label.trim() || !form.url.trim()) {
            toast.error("Label and URL are required.");
            return;
        }
        setBusy(true);
        try {
            await addPressAsset(form);
            toast.success("Asset added to the press kit.");
            setForm({ label: "", note: "", url: "" });
            setShowForm(false);
            await load();
        } catch {
            toast.error("Could not add. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (asset) => {
        if (!window.confirm(`Delete "${asset.label}" from the press kit?`)) return;
        try {
            await deletePressAsset(asset.id);
            toast.success("Asset deleted.");
            await load();
        } catch {
            toast.error("Could not delete. Please try again.");
        }
    };

    if (assets === null) return <p className="font-sub font-bold text-ink/60">Loading assets…</p>;

    return (
        <div data-testid="press-manager">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <p className="text-sm text-ink/70">{assets.length} assets in the press kit</p>
                <button
                    data-testid="press-asset-new-btn"
                    onClick={() => setShowForm((v) => !v)}
                    className="inline-flex items-center gap-2 bg-pink text-cream font-sub font-bold text-sm px-5 py-3 border-2 border-ink hard-shadow-sm transition-transform duration-200 hover:-translate-y-0.5"
                >
                    <Plus className="w-4 h-4" /> Add asset
                </button>
            </div>

            {showForm && (
                <form
                    onSubmit={add}
                    data-testid="press-asset-form"
                    className="mt-6 bg-paperalt border-2 border-ink hard-shadow p-6 grid sm:grid-cols-3 gap-4"
                >
                    <label className="block">
                        <span className="font-mono-brand text-[10px] uppercase tracking-[0.18em] text-ink/60">Label *</span>
                        <input
                            data-testid="press-asset-label-input"
                            className={`${inputCls} mt-1.5`}
                            value={form.label}
                            onChange={(e) => setForm({ ...form, label: e.target.value })}
                            placeholder="Episode 3 poster"
                        />
                    </label>
                    <label className="block">
                        <span className="font-mono-brand text-[10px] uppercase tracking-[0.18em] text-ink/60">Note</span>
                        <input
                            data-testid="press-asset-note-input"
                            className={`${inputCls} mt-1.5`}
                            value={form.note}
                            onChange={(e) => setForm({ ...form, note: e.target.value })}
                            placeholder="JPG, cleared for editorial use"
                        />
                    </label>
                    <label className="block sm:col-span-2">
                        <UploadField
                            label="File *"
                            testid="press-asset-file"
                            accept="image/*"
                            value={form.url}
                            onChange={(v) => setForm({ ...form, url: v })}
                        />
                    </label>
                    <button
                        type="submit"
                        data-testid="press-asset-save-btn"
                        disabled={busy}
                        className="sm:col-span-3 inline-flex items-center justify-center gap-2 bg-ink text-paper font-sub font-bold px-6 py-3 border-2 border-ink hard-shadow-pink transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
                    >
                        {busy ? "Adding…" : "Add to press kit"}
                    </button>
                </form>
            )}

            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {assets.map((a) => (
                    <div
                        key={a.id}
                        data-testid={`press-manager-asset-${a.id}`}
                        className="bg-cream border-2 border-ink hard-shadow-sm"
                    >
                        <div className="border-b-2 border-ink overflow-hidden">
                            <img src={a.url} alt={a.label} className="w-full aspect-square object-cover object-top" />
                        </div>
                        <div className="p-4 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <p className="font-sub font-bold text-sm text-ink truncate">{a.label}</p>
                                <p className="text-xs text-ink/60 truncate">{a.note}</p>
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <a
                                    href={a.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`press-manager-view-${a.id}`}
                                    aria-label={`Open ${a.label}`}
                                    className="p-2.5 bg-paper border-2 border-ink text-ink transition-colors duration-150 hover:bg-mustard/30"
                                >
                                    <Download className="w-4 h-4" />
                                </a>
                                <button
                                    data-testid={`press-manager-delete-${a.id}`}
                                    onClick={() => remove(a)}
                                    aria-label={`Delete ${a.label}`}
                                    className="p-2.5 bg-paper border-2 border-ink text-magenta transition-colors duration-150 hover:bg-magenta hover:text-cream"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
